import asyncEachSeries from 'async/eachSeries';
import asyncSeries from 'async/series';
import React from 'react';

import { StyleSheet, Text, View } from '../base_components';
import { BusyComponent } from '../busy_component';
import DialogOverlay from '../overlays/dialog_overlay';
import TextButton from '../buttons/text_button';
import ImageButton from '../buttons/image_button';
import ProgressSpinner from '../progress_spinner';

import EthTools from '../../tools/eth_tools';
import WalletStore from '../../stores/wallet_store';

const styles = StyleSheet.create({
  txOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  innerOverlay: {
    width: '29rem',
    paddingTop: '1.2rem',
    paddingBottom: '1.5rem',
    borderColor: '#ccc',
    borderWidth: 1,
    borderRadius: '1rem',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  title: {
    marginLeft: '1.5rem',
    color: '#333',
    fontSize: '2rem',
    fontWeight: '500',
  },
  closeButton: {
    position: 'absolute',
    top: '0.5rem',
    right: '0.5rem',
    width: '1.2rem',
    height: '1.2rem',
    padding: '0.9rem',
    borderRadius: '0.5rem',
  },
  description: {
    marginLeft: '1.5rem',
    marginRight: '1.5rem',
    color: 'black',
    fontSize: '1.4rem',
    lineHeight: '2rem',
    textAlign: 'center',
  },
  stepList: {
    marginTop: '2rem',
    marginLeft: '1.5rem',
    marginRight: '1.5rem',
    alignSelf: 'stretch',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  step: {
    marginTop: '1rem',
    alignSelf: 'stretch',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  circle: {
    marginRight: '1rem',
    width: '2rem',
    height: '2rem',
    backgroundColor: '#fefefe',
    borderWidth: '0.3rem',
    borderColor: 'black',
    borderRadius: '2rem',
  },
  doneCircle: {
    marginRight: '1rem',
    width: '2rem',
    height: '2rem',
    backgroundColor: 'green',
    borderWidth: '0.3rem',
    borderColor: 'green',
    borderRadius: '2rem',
  },
  stepText: {
    flex: 1,
    color: 'black',
    fontSize: '1.2rem',
    fontWeight: '600',
  },
  buttonView: {
    marginLeft: '1.5rem',
    marginRight: '1.5rem',
    marginTop: '2rem',
    height: '10rem',
    alignSelf: 'stretch',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  button: {
    alignSelf: 'stretch',
  },
  cancelButton: {
    marginTop: '1rem',
    alignSelf: 'stretch',
  },
  cancelButtonText: {
    color: 'white',
  },
});

const CLOSE_IMG = require('../../assets/img/close_black.png');

type Props = {
  style?: any;
  title: string;
  description?: string;
  data: any;
  onDone: () => void;
  onSuccess: (hash: string) => void;
  onError: (err: any) => void;
};
type State = {
  step_complete: any;
};
export default class TxOverlay extends BusyComponent<Props, State> {
  state = {
    step_complete: [],
  };
  _onContinuePress = () => {
    const { data } = this.props;
    if (this.setBusy()) {
      const opts = {
        data,
        step_success: this._stepSuccess,
      };
      _sendList(opts, (err, tx_hash_list, signed_tx_list) => {
        this.clearBusy();
        if (err === 'user_cancel') {
          this.setState({ step_complete: [] });
        } else if (err) {
          this.props.onError(err);
        } else {
          this.props.onSuccess(tx_hash_list, signed_tx_list);
        }
      });
    }
  };
  _stepSuccess = (index) => {
    const step_complete = this.state.step_complete.slice();
    step_complete[index] = true;
    this.setState({ step_complete });
  };

  render() {
    const { style, title, description, data, onDone } = this.props;
    const { step_complete, is_busy } = this.state;

    let content = null;
    const steps = data.step_list.map((step, i) => {
      return <Step key={i} step={step} isComplete={step_complete[i]} />;
    });
    let action = this._onContinuePress;
    let buttons = null;
    if (is_busy) {
      buttons = (
        <>
          <ProgressSpinner style={styles.spinner} color='black' />
        </>
      );
    } else {
      buttons = (
        <>
          <TextButton
            style={styles.button}
            type='default'
            text='Continue'
            onPress={action}
          />
          <TextButton
            style={styles.cancelButton}
            type='ghost-inverted'
            text='Back'
            onPress={onDone}
          />
        </>
      );
    }
    content = (
      <>
        <View style={styles.stepList}>{steps}</View>
        <View style={styles.buttonView}>{buttons}</View>
      </>
    );
    let desc = null;
    if (description) {
      desc = <Text style={styles.description}>{description}</Text>;
    }
    return (
      <View style={styles.txOverlay}>
        <DialogOverlay
          style={[styles.innerOverlay, style]}
          onOutsidePress={onDone}
        >
          <Text style={styles.title}>{title}</Text>
          {desc}
          {content}
          <ImageButton
            style={styles.closeButton}
            underlayColor='rgba(0,0,0,0.3)'
            source={CLOSE_IMG}
            onPress={onDone}
          />
        </DialogOverlay>
      </View>
    );
  }
}

function Step(props) {
  const { step, isComplete } = props;
  const { text, is_complete } = step;
  let circle = null;
  if (is_complete || isComplete) {
    circle = <View style={styles.doneCircle} />;
  } else {
    circle = <View style={styles.circle} />;
  }
  return (
    <View style={styles.step}>
      {circle}
      <Text style={styles.stepText}>{text}</Text>
    </View>
  );
}

function _sendList(params, done) {
  const { data, step_success } = params;
  const { precondition, step_list, success } = data;
  let index = 0;
  const tx_hash_list = [];
  const signed_tx_list = [];
  asyncSeries(
    [
      (done) => {
        if (precondition) {
          precondition(done);
        } else {
          done();
        }
      },
      (done) => {
        asyncEachSeries(
          step_list,
          (step, done) => {
            const opts = { step, signed_tx_list, tx_hash_list };
            _send(opts, (err, hash, signed_tx) => {
              if (!err) {
                step_success(index);
              }
              hash && tx_hash_list.push(hash);
              signed_tx && signed_tx_list.push(signed_tx);
              index++;
              done(err);
            });
          },
          done
        );
      },
      (done) => {
        if (success) {
          success(() => done());
        } else {
          done();
        }
      },
    ],
    (err) => {
      done(err, tx_hash_list, signed_tx_list);
    }
  );
}
function _send(params, done) {
  const { step, signed_tx_list, tx_hash_list } = params;
  const {
    tx,
    preflight,
    sign_only,
    callback,
    confirm_wait_count,
    chain_id,
    timeout,
  } = step;
  let skip = false;
  let tx_hash;
  let signed_tx;
  asyncSeries(
    [
      (done) => {
        if (preflight) {
          preflight((err, should_skip) => {
            skip = should_skip;
            done(err);
          });
        } else {
          done();
        }
      },
      (done) => {
        if (skip) {
          done();
        } else if (confirm_wait_count && !tx_hash_list[0]) {
          done();
        } else if (confirm_wait_count) {
          const opts = {
            chain_id,
            transaction_hash: tx_hash_list[0],
            block_count: confirm_wait_count,
            timeout,
          };
          EthTools.confirmTransaction(opts, (err) => {
            if (err === 'timeout') {
              err = null;
            }
            done(err);
          });
        } else if (callback) {
          callback({ tx_hash_list, signed_tx_list }, done);
        } else if (sign_only) {
          WalletStore.signTransaction(tx, (err, signed, hash) => {
            signed_tx = signed;
            tx_hash = hash;
            done(err);
          });
        } else {
          WalletStore.sendTransaction(tx, (err, hash) => {
            tx_hash = hash;
            done(err);
          });
        }
      },
    ],
    (err) => done(err, tx_hash, signed_tx)
  );
}
